/** "Probable source" banner (spec §4.2): the engine's suggested protective device
 *  with Confirm / Override, plus the convergence "Merge & assign" prompt when
 *  concurrent events point at the same chain. Source-device confirm is a SEPARATE
 *  action from reliability confirm (writes source_device_confirmed_id only).
 *
 *  Confirm/override thread expected_updated_at for optimistic concurrency and
 *  surface a 409 as refresh-&-retry (isConflictError), like the other operator
 *  dialogs. */

import { useState } from 'react'
import { isConflictError } from '../../api/client.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  useConfirmSourceDevice,
  useMergeOutages,
  useOutageConvergence,
  useOverrideSourceDevice,
} from '../../hooks/useOms.ts'
import type { OutageDetail } from '../../types/oms.ts'
import { formatSuggestion } from './omsFormat.tsx'
import { SourceDevicePicker } from './SourceDevicePicker.tsx'

function errorMessage(err: unknown): string {
  if (isConflictError(err)) {
    return 'This outage changed since you opened it. Refresh and re-apply.'
  }
  return friendlyError(err, 'update the source device')
}

export function SuggestedSourceBanner({
  outage,
  canMutate,
  onDone,
}: {
  outage: OutageDetail
  canMutate: boolean
  onDone?: (detail: OutageDetail) => void
}) {
  const [overriding, setOverriding] = useState(false)
  const [pickedDevice, setPickedDevice] = useState<number | null>(null)
  const confirmM = useConfirmSourceDevice()
  const overrideM = useOverrideSourceDevice()
  const mergeM = useMergeOutages()
  const { data: convergent } = useOutageConvergence(
    outage.suggested_device_id !== null ? outage.id : null,
  )

  if (outage.suggested_device_id === null) {
    return (
      <div className="border-t border-gray-100 pt-4">
        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">
          Probable source
        </div>
        <div className="text-xs text-gray-400">
          No source device suggested (cannot localize from current signals).
        </div>
      </div>
    )
  }

  const sug = formatSuggestion(outage.suggestion_detail, outage.suggested_device_confidence)
  const confirmedId = outage.source_device_confirmed_id
  const err = confirmM.error || overrideM.error || mergeM.error

  function confirm(expectedUpdatedAt: string = outage.updated_at) {
    confirmM.mutate(
      { id: outage.id, body: { expected_updated_at: expectedUpdatedAt, notes: null } },
      { onSuccess: d => onDone?.(d) },
    )
  }
  function submitOverride() {
    if (pickedDevice === null) return
    overrideM.mutate(
      {
        id: outage.id,
        body: {
          expected_updated_at: outage.updated_at,
          device_id: pickedDevice,
          notes: null,
        },
      },
      {
        onSuccess: d => {
          setOverriding(false)
          setPickedDevice(null)
          onDone?.(d)
        },
      },
    )
  }
  function mergeAndAssign() {
    if (!convergent || convergent.length === 0) return
    // Compose the EXISTING merge + the source-device confirm (no auto-merge; the
    // operator clicked). Re-parent the convergent siblings under this event, then
    // confirm the shared suggested device. The merge advances the primary's
    // updated_at, so the chained confirm MUST thread the merged detail's fresh
    // updated_at — the pre-merge closure value would reliably 409.
    mergeM.mutate(
      {
        primary_id: outage.id,
        merge_ids: convergent.map(c => c.id),
        expected_updated_at: outage.updated_at,
        notes: 'merge & assign (convergent source device)',
      },
      { onSuccess: merged => confirm(merged.updated_at) },
    )
  }

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">
        Probable source
      </div>
      <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
        <div className="text-sm text-amber-900">
          Probable source: <span className="font-semibold">{sug?.label}</span>
          {sug ? ` (${sug.coverage}${sug.confidence ? `, ${sug.confidence}` : ''})` : ''}
        </div>
        {confirmedId !== null && (
          <div className="text-xs text-emerald-700 mt-1">
            Source device confirmed (device #{confirmedId}).
          </div>
        )}
        {err && (
          <div
            role="alert"
            className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-2"
          >
            {errorMessage(err)}
          </div>
        )}
        {canMutate && confirmedId === null && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              onClick={() => confirm()}
              disabled={confirmM.isPending}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            >
              {confirmM.isPending ? 'Confirming…' : 'Confirm source'}
            </button>
            <button
              onClick={() => setOverriding(v => !v)}
              className="text-xs px-3 py-1.5 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {overriding ? 'Cancel override' : 'Override…'}
            </button>
          </div>
        )}
        {canMutate && overriding && (
          <div className="mt-2">
            <SourceDevicePicker
              feederId={outage.picker_feeder_id}
              value={pickedDevice}
              onChange={setPickedDevice}
            />
            <button
              onClick={submitOverride}
              disabled={pickedDevice === null || overrideM.isPending}
              className="mt-2 text-xs px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {overrideM.isPending ? 'Saving…' : 'Save override'}
            </button>
          </div>
        )}
        {canMutate && convergent && convergent.length > 0 && confirmedId === null && (
          <div className="mt-2 border-t border-amber-200 pt-2">
            <div className="text-xs text-amber-800">
              {convergent.length} other active outage
              {convergent.length === 1 ? '' : 's'} point to the same device chain.
            </div>
            <button
              onClick={mergeAndAssign}
              disabled={mergeM.isPending || confirmM.isPending}
              className="mt-1 text-xs px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
            >
              {mergeM.isPending ? 'Merging…' : 'Merge & assign'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
